import { BBROKER_STOCK_CATALOG } from './bbrokerStockCatalog.js';

const ADDRESS = /^0x[a-f0-9]{40}$/;
const known = new Map([
  ['0x' + '0'.repeat(40), 18],
  ['0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c', 18],
  ['0x55d398326f99059ff775485246999027b3197955', 18],
  ...BBROKER_STOCK_CATALOG.filter((asset) => Number.isInteger(asset.decimals))
    .map((asset) => [asset.address.toLowerCase(), asset.decimals]),
]);
const cache = new Map();
const pending = new Map();
const valid = (value) => value != null && Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 255;

export const quoteDecimalsAbi = [{ type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] }];
export function knownQuoteDecimals(address) { return known.get(String(address || '').toLowerCase()) ?? null; }

async function boundedMetadataRead(readBatch, addresses) {
  let timer;
  try {
    return await Promise.race([
      Promise.resolve().then(() => readBatch(addresses)),
      new Promise((resolve) => { timer = setTimeout(() => resolve([]), 1500); }),
    ]);
  } catch { return []; }
  finally { clearTimeout(timer); }
}

/** Reader returns one decimals value (or null) per address; failures never assume 18. */
export async function readQuoteDecimals(addresses, readBatch) {
  const list = [...new Set(addresses.map((address) => String(address || '').toLowerCase()))].filter((address) => ADDRESS.test(address));
  const out = {};
  const need = [];
  const waits = [];
  for (const address of list) {
    const fixed = knownQuoteDecimals(address);
    const hit = cache.get(address);
    if (fixed != null) out[address] = fixed;
    else if (hit && Date.now() - hit.at < (hit.value == null ? 30000 : 3600000)) out[address] = hit.value;
    else if (pending.has(address)) waits.push(pending.get(address).then((value) => { out[address] = value; }));
    else need.push(address);
  }
  if (need.length) {
    const batch = boundedMetadataRead(readBatch, need);
    for (const [index, address] of need.entries()) {
      const work = batch.then((values) => {
        const value = valid(values?.[index]) ? Number(values[index]) : null;
        cache.set(address, { value, at: Date.now() });
        while (cache.size > 512) cache.delete(cache.keys().next().value);
        out[address] = value;
        return value;
      }).finally(() => pending.delete(address));
      pending.set(address, work);
      waits.push(work);
    }
  }
  await Promise.all(waits);
  return out;
}
